import { t } from "@lingui/macro";
import { ArrowClockwise, Warning, X } from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { Component, ReactNode } from "react";

interface AnalysisErrorBoundaryProps {
  children: ReactNode;
  onRegenerate: () => void;
  onClose?: () => void;
  isGenerating?: boolean;
}

interface AnalysisErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: string;
}

export class AnalysisErrorBoundary extends Component<
  AnalysisErrorBoundaryProps,
  AnalysisErrorBoundaryState
> {
  constructor(props: AnalysisErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): AnalysisErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Analysis rendering error:", error, errorInfo);
    this.setState({
      hasError: true,
      error,
      errorInfo: errorInfo.componentStack || undefined,
    });
  }

  handleRegenerate = () => {
    // Reset error state
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    // Call the regenerate function
    this.props.onRegenerate();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center space-y-6 py-12 text-center">
          <div className="bg-destructive/10 rounded-full p-4">
            <Warning className="text-destructive size-12" />
          </div>

          <div className="max-w-md space-y-2">
            <h3 className="text-destructive text-xl font-bold">{t`Analysis Rendering Error`}</h3>
            <p className="text-muted-foreground text-sm">
              {t`There was an error displaying the analysis results. You can regenerate the analysis to try again.`}
            </p>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <Button onClick={this.handleRegenerate} disabled={this.props.isGenerating}>
              {this.props.isGenerating ? (
                <>
                  <ArrowClockwise className="mr-2 size-4 animate-spin" />
                  {t`Regenerating...`}
                </>
              ) : (
                <>
                  <ArrowClockwise className="mr-2 size-4" />
                  {t`Regenerate Analysis`}
                </>
              )}
            </Button>

            {this.props.onClose && (
              <Button onClick={this.props.onClose} variant="ghost" size="icon">
                <X className="size-4" />
              </Button>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
